/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button } from '@mui/material';

import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { SelectItemCatalogue } from '../../../../components/SelectItemCatalogue/SelectItemCatalogue';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { Namespaces } from '../../../../i18n';
import { useSelectEntityModalStyles } from '../../../../styles/selectEntityModal';
import { Organization } from '../../../../types/organization/Organization';
import { CreateProjectFormValues } from '../../../CreateProjectOverlay/CreateProjectOverlay.types';
import { TPickerModal } from '../types';
import { usePickerModalStyles } from './PickerModal.styles';

export const PickerModal = memo<TPickerModal>(
  ({ values, setValues, pickedState, setPickedState, organizations, isFetchingOrganizations }) => {
    const [modalOpen, setModalOpen] = useState(false);

    const { t } = useTranslation([Namespaces.CreateProject, Namespaces.Common]);

    const classes = usePickerModalStyles();
    const modalClasses = useSelectEntityModalStyles();

    const handleToggleModal = () => setModalOpen(prevState => !prevState);

    const handleAddClick = () => {
      handleToggleModal();
      setValues({
        ...(values as CreateProjectFormValues),
        organizationId: pickedState.organization?.id,
        organization: pickedState.organization || undefined,
      });
    };

    const handleSelect = () => (_e: unknown, value: Organization) =>
      setPickedState(prevState => ({
        ...prevState,
        organization: value,
      }));

    const getOptionLabel = (value: unknown) => (value as Organization).name;

    const getOptionSelected = (value: Organization, selected: unknown) =>
      value.id === (selected as Organization | undefined)?.id;

    const buttonText =
      (values?.organization as Organization | undefined)?.name ?? t(`${Namespaces.Entities}:organization.select`);

    const modalTitle = t(`${Namespaces.Entities}:organization.select`);

    const isAddButtonDisabled = !pickedState.organization || isFetchingOrganizations;

    const modalFooter = ({ footerClass }: { footerClass: string }) => (
      <div className={footerClass}>
        <Button
          color="primary"
          variant="contained"
          disabled={isAddButtonDisabled}
          onClick={handleAddClick}
          data-cy="picker-modal-submit-button"
        >
          {t('templates.add_to_organization', {
            organization: pickedState.organization?.name || '...',
          })}
        </Button>
        <Button color="primary" variant="text" onClick={handleToggleModal} data-cy="picker-modal-cancel-button">
          {t('labels.cancel')}
        </Button>
      </div>
    );

    const modalContent = (
      <div className={cx(modalClasses.row)}>
        <Box width="100%">
          <SelectItemCatalogue
            options={organizations ?? []}
            selected={pickedState?.organization}
            getOptionLabel={getOptionLabel}
            getOptionSelected={getOptionSelected}
            onItemClick={handleSelect()}
            title={t('labels.organizations')}
            buttonProps={{ disableRipple: true }}
            iconType="check"
          />
        </Box>
      </div>
    );

    const buttonVariant = values['organization'] ? 'contained' : 'outlined';

    return (
      <>
        <div className={classes.root}>
          <Button
            variant={buttonVariant}
            onClick={handleToggleModal}
            className={classes.button}
            size="large"
            data-cy="picker-modal-button"
          >
            {buttonText}
            <ArrowRoundedIcon className={classes.buttonIcon} />
          </Button>
        </div>
        <HUModal2
          open={modalOpen}
          size="medium"
          title={modalTitle}
          paperClassName={modalClasses.modalPaper}
          footer={modalFooter}
        >
          <div className={modalClasses.body}>{modalContent}</div>
        </HUModal2>
      </>
    );
  },
);
